import { Button, FilterMenu, PageHeader, SearchBox, type Column } from '@platform/design-system';
import type { ShippingClient, TypedClient } from '@platform/openapi';
import { useState, type ReactNode } from 'react';
import { namesOf, useGeoAreas } from '../geography/useGeoAreas.ts';
import tools from '../ListTools.module.css';
import { OrderFilterBar } from '../orders/OrderFilterBar.tsx';
import {
  isFiltered,
  narrowingQueryOf,
  NO_ORDER_FILTERS,
  type OrderFilters,
} from '../orders/order-filters.ts';
import { OrderPanels } from '../orders/OrderPanels.tsx';
import { useOrderMoves } from '../orders/useOrderMoves.ts';
import type { Order, OrderStatus, OrderView } from '../orders/orders.ts';
import { useOrderColumns } from '../orders/useOrderColumns.tsx';
import { ORDER_PAGE_SIZE, useOrderList } from '../orders/useOrderList.ts';
import { ResourceTable } from '../ResourceTable.tsx';
import { useDebouncedValue } from '../useDebouncedValue.ts';
import { usePageContext } from '../usePageContext.ts';
import { PARCEL_COLUMN_IDS, type ParcelListKind } from './parcels.ts';

const SEARCH_DELAY_MS = 300;

export type StatusFilter = {
  readonly value: OrderStatus | null;
  readonly options: readonly OrderStatus[];
  readonly onChange: (status: OrderStatus | null) => void;
};

export type ParcelWorklistProps = {
  api: TypedClient;
  shipping: ShippingClient;
  kind: ParcelListKind;
  view: OrderView;
  filters: OrderFilters;
  statusFilter: StatusFilter | null;
  timingColumn: Column<Order>;
  action?: ReactNode;
  openOrderId: string | null;
  onFiltersChange: (filters: OrderFilters) => void;
  onOpenOrder: (orderId: string | null) => void;
  onEdit: (orderId: string) => void;
  onOpenSettings: () => void;
};

const emptyKeyOf = (kind: ParcelListKind, narrowed: boolean): string =>
  narrowed ? 'parcels.noMatch' : `parcels.${kind}Empty`;

export const ParcelWorklist = (props: ParcelWorklistProps) => {
  const { api, shipping, kind, view, filters, statusFilter, timingColumn, action } = props;
  const { openOrderId, onFiltersChange, onOpenOrder } = props;
  const moves = useOrderMoves(api, props.onEdit);
  const status = statusFilter?.value ?? null;
  const { t } = usePageContext();
  const [search, setSearch] = useState('');
  const [openedAt] = useState(() => new Date());
  const text = useDebouncedValue(search.trim(), SEARCH_DELAY_MS);
  const list = useOrderList(api, view, narrowingQueryOf(filters, openedAt), { status, text });
  const geoNames = namesOf(useGeoAreas(api).data);
  const orderColumns = useOrderColumns(shipping, geoNames);
  const columns = [
    ...PARCEL_COLUMN_IDS.flatMap((id) => orderColumns.filter((column) => column.id === id)),
    timingColumn,
  ];
  const narrowed = text !== '' || status !== null || isFiltered(filters);
  const emptyKey = emptyKeyOf(kind, narrowed);
  const clear = () => {
    setSearch('');
    statusFilter?.onChange(null);
    onFiltersChange(NO_ORDER_FILTERS);
  };

  return (
    <>
      <PageHeader
        title={t(`parcels.${kind}Title`)}
        context={t(`parcels.${kind}Context`, { count: list.total })}
        action={action}
      />
      <ResourceTable
        query={list.query}
        columns={columns}
        rows={list.items}
        rowKey={(order) => order.id}
        activeKey={openOrderId}
        onRowClick={(order) => {
          onOpenOrder(order.id);
        }}
        toolbar={
          <div className={tools['listTools']}>
            <SearchBox
              label={t('parcels.searchLabel')}
              placeholder={t('parcels.searchPlaceholder')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
              onSearch={setSearch}
            />
            {statusFilter === null ? null : (
              <FilterMenu
                label={t('orders.status')}
                options={statusFilter.options.map((option) => ({
                  value: option,
                  label: t(`orders.status_${option}`),
                }))}
                value={status}
                clearLabel={t('orders.filter_remove', { name: t('orders.status') })}
                onChange={(value) => {
                  statusFilter.onChange(statusFilter.options.find((option) => option === value) ?? null);
                }}
              />
            )}
            <OrderFilterBar api={api} shipping={shipping} filters={filters} onChange={onFiltersChange} />
            {narrowed ? (
              <Button variant="ghost" size="sm" onClick={clear}>
                {t('common.clearFilters')}
              </Button>
            ) : null}
          </div>
        }
        pagination={{
          page: list.page,
          pageSize: ORDER_PAGE_SIZE,
          total: list.total,
          onPageChange: list.turnTo,
        }}
        emptyTitle={t(emptyKey)}
        emptyDescription={t(`${emptyKey}Help`)}
      />
      <OrderPanels {...props} geoNames={geoNames} moves={moves} />
    </>
  );
};
